import { fetchConfig, trapFocus, removeTrapFocus, isJson, handlePurchaseLimitErrors } from '../common'
import { UmgGwp } from './umg-gwp'

class CartDrawer extends HTMLElement {
  constructor () {
    super()

    this.drawer = this
    this.onBodyClick = this.handleBodyClick.bind(this)

    document.addEventListener('keyup', (evt) => evt.key === 'Escape' && this.close())

    this.setupCloseButton()

    this.isOpen = false
    this.contentOverlay = document.querySelector('content-overlay')

    window.addEventListener('pageshow', this.onHistoryChange.bind(this))
    document.addEventListener('visibilitychange', this.onTabChange.bind(this))
    if (document.querySelector('[data-pause-cart-countdown]')) {
      import('./pause-cart-countdown').then((module) => {
        module.initPauseCartCountdown(this)
      })
    }
  }

  setupCloseButton () {
    this.closeIcon = this.drawer.querySelector('.cart-drawer__close')
    this.closeIcon.addEventListener('click', () => this.close())
  }

  open (triggerGtm = true) {
    this.contentOverlay.show()
    this.drawer.removeAttribute('hidden')
    setTimeout(() => { this.drawer.classList.add('animate', 'active') }, 1)
    document.body.classList.add('overflow-hidden')

    this.drawer.addEventListener('transitionend', () => {
      this.drawer.focus()
      trapFocus(this.drawer)
    }, { once: true })

    document.body.addEventListener('click', this.onBodyClick)

    this.isOpen = true

    if (!Shopify.designMode && triggerGtm) {
      let value = 0
      if (this.querySelector('[name="gtm_cart_value"]')) {
        value = this.querySelector('[name="gtm_cart_value"]').getAttribute('value')
      }
      let giftedVal = false
      if (this.querySelector('[name="gtm_cart_gifted"]')) {
        giftedVal = this.querySelector('[name="gtm_cart_gifted"]').getAttribute('value')
      }
      const gifted = (giftedVal === 'true')
      const cartItems = this.querySelectorAll('.cart__item')
      const items = []
      for (const cartItem of cartItems) {
        const gtm_item_name = cartItem.querySelector('[name="gtm_item_name"]').getAttribute('value')
        const gtm_item_id = cartItem.querySelector('[name="gtm_item_id"]').getAttribute('value')
        const gtm_item_variant = cartItem.querySelector('[name="gtm_item_variant"]').getAttribute('value')
        const gtm_item_upc = cartItem.querySelector('[name="gtm_item_upc"]').getAttribute('value')
        const gtm_item_price = cartItem.querySelector('[name="gtm_item_price"]').getAttribute('value')
        const gtm_item_brand = cartItem.querySelector('[name="gtm_item_brand"]')?.getAttribute('value') || ''
        const gtm_item_sku = cartItem.querySelector('[name="gtm_item_sku"]')?.getAttribute('value')
        const gtm_item_group_id = cartItem.querySelector('[name="gtm_item_group_id"]')?.getAttribute('value') || ''
        const gtm_item_category = cartItem.querySelector('[name="gtm_item_category"]')?.getAttribute('value') || ''
        const gtm_item_quantity = cartItem.getAttribute('qty')
        const item = {
          item_name: gtm_item_name,
          item_id: gtm_item_id,
          item_variant: gtm_item_variant,
          upc: gtm_item_upc,
          price: parseFloat(gtm_item_price),
          quantity: parseInt(gtm_item_quantity),
        }

        if (window.shop_iso_code === 'DE') {
          Object.assign(item, {
            item_brand: gtm_item_brand,
            item_sku: gtm_item_sku,
            item_group_id: gtm_item_group_id,
            category: gtm_item_category,
            barcode: gtm_item_upc,
          })
        }
        items.push(item)
      }
      Shopify.analytics.publish('view_cart', {
        currency: Shopify.currency.active,
        value: parseFloat(value),
        items: items,
        gift: gifted
      })
    }
  }

  close () {
    this.contentOverlay.hide()
    setTimeout(() => { this.drawer.setAttribute('hidden', true) }, 300)
    this.drawer.classList.remove('active')
    // this.drawerWrapper.classList.remove('active')
    document.body.classList.remove('overflow-hidden')

    document.body.removeEventListener('click', this.onBodyClick)

    removeTrapFocus(this.activeElement)

    this.isOpen = false
  }

  renderContents (parsedState, openCart = true, triggerGtm = false) {
    this.getSectionsToRender().forEach(section => {
      document.getElementById(section.id).innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector)
      if (document.getElementById(`${section.id}_md`)) {
        document.getElementById(`${section.id}_md`).innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector)
      }
    })

    if (openCart) {
      this.open(triggerGtm)
      this.setupCloseButton()
    }
    if (document.querySelector('[data-pause-cart-countdown]')) {
      import('./pause-cart-countdown').then((module) => {
        module.initPauseCartCountdown(this)
      })
    }
  }

  getSectionsToRender () {
    return [
      {
        id: 'cart-drawer'
      },
      {
        id: 'header-cart-count'
      }
    ]
  }

  getSectionInnerHTML (html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML
  }

  handleBodyClick (evt) {
    const target = evt.target
    if (!target.closest('cart-drawer-toggle-button') && target !== this.drawer && !target.closest('cart-drawer')) {
      const disclosure = target.closest('details-disclosure')
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null
      this.close()
    }
  }

  setActiveElement (element) {
    this.activeElement = element
  }

  renderProductInfo (sectionId, variantId) {
    // variant id in place so variant doesn't reset on item remove or update
    fetch(`${window.location.pathname}?section=${sectionId}`)
      .then((response) => response.text())
      .then((responseText) => {
        const id = `${sectionId}`
        const html = new DOMParser().parseFromString(responseText, 'text/html')
        const source = html.getElementById(id)
        const destination = document.getElementById(id)
        if (source && destination) destination.innerHTML = source.innerHTML
        // This event is listened by pause-detail-page to go through the 'checkPauseVariants' function and update pdp atc button state
        const variantChangedEvent = new CustomEvent('umg:variant:changed', {
          detail: {
            variantId: variantId,
            sectionId: this.dataset.section
          }
        })
        document.dispatchEvent(variantChangedEvent)
      })
  }

  updateQuantity (line, quantity, variantId, qtyChange, gtmContent) {
    // freeze cart drawer here, similar to freeze on PDP
    const pdpQuantitySpinnerId = `.item-quantity quantity-spinner[data-variant-id="${variantId}"]`
    const pdpQuantitySelectorId = `.item-quantity quantity-selector[data-variant-id="${variantId}"]`

    let pdpQuantityInput = null
    let pdpQuantityInputSections = []
    if (document.querySelector(pdpQuantitySpinnerId)) {
      pdpQuantityInput = document.querySelector(pdpQuantitySpinnerId)
      pdpQuantityInputSections = pdpQuantityInput.getSectionsToRender().map((section) => section.id)
    } else if (document.querySelector(pdpQuantitySelectorId)) {
      pdpQuantityInput = document.querySelector(pdpQuantitySelectorId)
      pdpQuantityInputSections = pdpQuantityInput.getSectionsToRender().map((section) => section.id)
    }

    const sections = this.getSectionsToRender().map((section) => section.id).concat(pdpQuantityInputSections)
    const sections_url = window.location.pathname

    const body = JSON.stringify({
      line,
      quantity,
      sections: sections,
      sections_url: sections_url,
      selector: '.shopify-section'
    })
    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.json()
      })
      .then((parsedState) => {
        if (parsedState.errors !== undefined) {
          if (pdpQuantityInput) {
            const component_ref = gtmContent.item_component_ref
            const product_data = {
              id: gtmContent.item_id,
              component_ids: component_ref ? component_ref.split(',') : null
            }

            handlePurchaseLimitErrors(product_data, parsedState)
          }
        } else {
          this.renderContents(parsedState)
          if (pdpQuantityInput) {
            pdpQuantityInput.renderContent(parsedState)
          }

          if (!Shopify.designMode) {
            const customEvent = {
              currency: window.currentCurrency,
              items: [
                {
                  item_name: gtmContent.item_name,
                  item_id: gtmContent.item_id,
                  item_category: gtmContent.item_collection,
                  item_variant: gtmContent.item_variant,
                  upc: gtmContent.item_upc,
                  price: parseFloat(gtmContent.item_price),
                  quantity: parseInt(gtmContent.quantity),
                }
              ]
            }

            if (window.shop_iso_code === 'DE') {
              const item = {
                ...customEvent.items[0],
                item_brand: gtmContent.item_brand,
                item_sku: gtmContent.item_sku,
                item_group_id: gtmContent.item_group_id,
                category: gtmContent.category
              }

              Object.assign(customEvent, {
                items: [item]
              })
            }

            if (qtyChange > 0) {
              Shopify.analytics.publish('cart_item_added', customEvent)
            } else {
              Shopify.analytics.publish('cart_item_removed', customEvent)
            }
          }

          if (window.umgGwp !== undefined) {
            // Don't call umg gift with purchase if no gift with purchase data exists
            UmgGwp(this.drawer, sections, sections_url)
          }
        }
      }).catch((errors) => {
        console.error('errors', errors)
        errors.forEach(error => {
          if (pdpQuantityInput) {
            pdpQuantityInput.showPurchaseLimitErrorMessage(error)
          }
        })
      }).finally(() => {
        // update pdp
        const pdpInfoId = 'product__info'
        if (document.getElementsByClassName(pdpInfoId)[0] !== undefined) {
          const sectionId = document.getElementsByClassName(pdpInfoId)[0].getAttribute('id')
          this.renderProductInfo(sectionId, variantId)
        }

        this.setupCloseButton()
      })
  }

  onHistoryChange (event) {
    if (event.persisted) {
      // this.refreshCart()
      window.refreshSections(this.getSectionsToRender(), false)
    }
  }

  onTabChange (event) {
    // console.log(event)
    if (!document.hidden) {
      // console.log('Browser tab is visible')
      // this.refreshCart()
      window.refreshSections(this.getSectionsToRender(), false)
    }
  }

  // refreshCart () {
  //   console.log('getting cart sections:', this.getSectionsToRender())
  //   window.refreshSections(this.getSectionsToRender())
  // //   const sections = this.getSectionsToRender().map((section) => section.id)
  // //   fetch(`?sections=${sections}`, {})
  // //     .then((response) => {
  // //       return response.text()
  // //     })
  // //     .then((state) => {
  // //       const parsedState = JSON.parse(state)
  // //       this.renderContents({
  // //         sections: parsedState
  // //       }, false)
  // //     }).catch(() => {
  // //     })
  //   // window.refreshSections('cart')
  // }
}

customElements.define('cart-drawer', CartDrawer)

// TODO: Change to Alpine:
class CartDrawerToggleButton extends HTMLElement {
  constructor () {
    super()
    this.previousFocusTarget = this
    this.addEventListener('click', this.clickCartDrawerToggleButton)
    this.addEventListener('keyup', (event) => {
      if (event.key === 'Enter') {
        return this.clickCartDrawerToggleButton(event)
      }
    })
  }

  clickCartDrawerToggleButton (event) {
    event.preventDefault()
    const drawer = document.querySelector('cart-drawer')
    if (drawer) {
      if (drawer.isOpen) {
        drawer.close()
        this.previousFocusTarget.focus()
      } else {
        drawer.open()
        const cartCloseButton = drawer.querySelector('.cart-drawer__close')
        if (cartCloseButton) {
          setTimeout(() => { cartCloseButton.focus() }, 300)
        }
        drawer.querySelector('#cart-drawer-checkout .cart-drawer__link')?.focus()
      }
    } else {
      window.location.href = routes.cart_url
    }
  }
}

customElements.define('cart-drawer-toggle-button', CartDrawerToggleButton)
